import React, {
  Children,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { LayoutEvent, LayoutValue } from "@design-system/hooks"
import { useTabsContext } from "./TabsContext"
import { TabListProps } from "./types"

export interface UseTabListResult extends TabListProps {
  swipeableHeight: number
  hidePrevButton: boolean
  hideNextButton: boolean
  listRef: React.RefObject<HTMLDivElement>
  childLayouts: Map<number, LayoutValue>
  setTabContainerRef: (index: number) => (ref: HTMLElement | null) => void
  goToPreviousPage: () => void
  goToNextPage: () => void
  handlePrevButtonLayout: (width: number) => void
  handleNextButtonLayout: (width: number) => void
  handleSwipeableLayout: (e: LayoutEvent) => void
  getHandleItemClick: (index: number) => () => void
  handleKeyDown: (e: React.KeyboardEvent) => void
  updateChildLayout: (layout: LayoutValue, index: number) => void
  getHandleChildLayout: (index: number) => (e: LayoutEvent) => void
}

export function useTabList(props: TabListProps): UseTabListResult {
  const { setSelectedIndex, setFocusedIndex, focusedIndex } = useTabsContext()

  const { style, children, onVisibleRangeChange, ...htmlProps } = props

  const [prevButtonWidth, setPrevButtonWidth] = useState(0)
  const [nextButtonWidth, setNextButtonWidth] = useState(0)
  const [swipeableWidth, setSwipeableWidth] = useState(0)
  const [swipeableHeight, setSwipeableHeight] = useState(0)
  const [hidePrevButton, setHidePrevButton] = useState(true)
  const [hideNextButton, setHideNextButton] = useState(true)
  const [childLayouts, setChildLayouts] = useState(
    new Map<number, LayoutValue>(),
  )
  const focusedTabIndexRef = useRef(focusedIndex || 0)
  const listRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef(new Map<number, HTMLElement>())
  const observer = useRef<IntersectionObserver>()

  const setTabContainerRef = (index: number) => (ref: HTMLElement | null) => {
    if (ref) {
      tabRefs.current.set(index, ref)
    }
  }

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      entries.forEach((entry) => {
        const index = Array.from(
          observer.root?.firstElementChild?.children ?? [],
        ).indexOf(entry.target)
        const { intersectionRatio } = entry
        if (index === 0) {
          setHidePrevButton(intersectionRatio === 1)
        } else if (index === Children.count(children) - 1) {
          setHideNextButton(intersectionRatio === 1)
        }
        if (index !== -1) {
          onVisibleRangeChange?.({
            index,
            intersectionRatio,
          })
        }
      })
    },
    [children],
  )

  useEffect(() => {
    if (window.IntersectionObserver) {
      const root = listRef.current
      observer.current = new window.IntersectionObserver(handleIntersection, {
        root,
        rootMargin: "5px",
        threshold: [0.5, 1],
      })
      tabRefs.current.forEach((tabRef, i) => {
        if (tabRef && tabRef instanceof HTMLElement) {
          observer.current?.observe(tabRef)
        }
      })
    }
    return () => {
      observer.current?.disconnect()
    }
  }, [])

  useEffect(() => {
    if (focusedIndex !== focusedTabIndexRef.current) {
      goToItem(focusedIndex)
    }
  }, [focusedIndex])

  useEffect(() => {
    if (listRef.current && (prevButtonWidth || nextButtonWidth)) {
      // @ts-ignore
      listRef.current.style.scrollPadding = prevButtonWidth || nextButtonWidth
    }
  }, [listRef.current, prevButtonWidth, nextButtonWidth])

  const getLeftOffset = useCallback(() => {
    const node = listRef.current
    if (node && node instanceof HTMLElement) {
      return node.scrollLeft
    }
    return 0
  }, [listRef.current])

  const scrollToOffset = useCallback(
    (left: number) => {
      const node = listRef.current
      if (node && node instanceof HTMLElement) {
        node.scrollTo({
          left,
          behavior: "smooth",
        })
      }
    },
    [listRef.current],
  )

  const goToPreviousPage = useCallback(() => {
    scrollToOffset(getLeftOffset() - swipeableWidth)
  }, [swipeableWidth])

  const goToNextPage = useCallback(() => {
    scrollToOffset(getLeftOffset() + swipeableWidth)
  }, [swipeableWidth])

  const goToItem = useCallback(
    (index: number) => {
      if (tabRefs.current.has(index)) {
        const node = tabRefs.current.get(index)
        if (node && node instanceof HTMLElement) {
          if (node.scrollIntoView) {
            try {
              node.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "nearest",
              })
            } catch (e) {
              node.scrollIntoView(false)
            }
          }
          node.focus({
            preventScroll: true,
          })
        }
      }
      focusedTabIndexRef.current = index
      setFocusedIndex(index)
    },
    [tabRefs.current],
  )

  const handlePrevButtonLayout = useCallback((width: number) => {
    setPrevButtonWidth(width)
  }, [])

  const handleNextButtonLayout = useCallback((width: number) => {
    setNextButtonWidth(width)
  }, [])

  const handleSwipeableLayout = useCallback(
    (e: LayoutEvent) => {
      const { width, height } = e.nativeEvent.layout
      if (width !== swipeableWidth || height !== swipeableHeight) {
        setSwipeableWidth(width)
        setSwipeableHeight(height)
      }
    },
    [swipeableWidth, swipeableHeight],
  )

  const getHandleItemClick = (index: number) => () => {
    goToItem(index)
    setSelectedIndex(index)
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const { key } = e

      if (!["ArrowLeft", "ArrowRight", "Enter"].includes(key)) {
        return
      }

      e.preventDefault()
      const tabsCount = Children.count(children)
      let focusedTabIndex = focusedTabIndexRef.current

      if (key === "Enter") {
        setSelectedIndex(focusedTabIndex)
      }

      if (key === "ArrowLeft") {
        focusedTabIndex =
          focusedTabIndex === 0 ? tabsCount - 1 : focusedTabIndex - 1
      }

      if (key === "ArrowRight") {
        focusedTabIndex =
          focusedTabIndex === tabsCount - 1 ? 0 : focusedTabIndex + 1
      }

      goToItem(focusedTabIndex)
    },
    [focusedTabIndexRef.current],
  )

  const updateChildLayout = useCallback(
    (layout: LayoutValue, index: number) => {
      const oldLayout = childLayouts.get(index)
      if (
        !oldLayout ||
        oldLayout.x !== layout.x ||
        oldLayout.y !== layout.y ||
        oldLayout.width !== layout.width ||
        oldLayout.height !== layout.height
      ) {
        setChildLayouts((layouts) => {
          const newLayouts = new Map(layouts)
          newLayouts.set(index, layout)
          setChildLayouts(newLayouts)
          return newLayouts
        })
      }
    },
    [childLayouts],
  )

  const getHandleChildLayout = (index: number) => (e: LayoutEvent) =>
    updateChildLayout(e.nativeEvent.layout, index)

  return {
    swipeableHeight,
    hidePrevButton,
    hideNextButton,
    listRef,
    childLayouts,
    setTabContainerRef,
    goToPreviousPage,
    goToNextPage,
    handlePrevButtonLayout,
    handleNextButtonLayout,
    handleSwipeableLayout,
    getHandleItemClick,
    handleKeyDown,
    updateChildLayout,
    getHandleChildLayout,
    children,
    ...htmlProps,
  }
}
